import { DialogComponent } from '@theme/dialog';

const SEEN_KEY = 'cb-newsletter-popup-seen';
const CAPTCHA_WAIT_MS = 6000;
const MOBILE_QUERY = '(max-width: 749px)';

/**
 * Newsletter popup that opens once per browser session.
 *
 * - Opens after `data-delay` seconds unless it was already seen this session
 *   or the section is switched off (`data-disabled`).
 * - On phones with `data-mobile-scroll`, the delay starts only once the
 *   visitor has scrolled one screen down, so the popup never covers the page
 *   on arrival (Google treats that as an intrusive interstitial).
 * - Always opens when the dialog carries a form result (`data-state`), so the
 *   visitor sees the success or error message after the page reloads.
 * - In the theme editor it never opens on its own; selecting the section
 *   previews it and deselecting closes it.
 * - Checks the email before submitting: a missing or malformed address shows
 *   the error message in the open popup instead of posting the form, which
 *   would reload the page and close the popup.
 * - Posts the form in the background so Shopify's answer (subscribed, already
 *   subscribed, invalid email, any other error) replaces the popup body in
 *   place. Shopify's captcha still runs first: its script calls form.submit()
 *   (overridden below) or requestSubmit() once it has a token. If Shopify asks
 *   for its /challenge page, or the answer can't be read, the browser falls
 *   back to the normal full-page submit.
 * - Shopify's captcha binds to the form on its first focus and needs about a
 *   second to load; a submit before that is swallowed by Shopify's script. So
 *   the captcha is warmed up when the popup opens, and an early submit waits
 *   for it (up to CAPTCHA_WAIT_MS) and then goes through on its own.
 * - While the captcha runs the dialog is switched from modal to non-modal
 *   (.is-captcha): a modal dialog sits in the top layer and makes the rest of
 *   the page inert, so an hCaptcha puzzle opened on the page would be hidden
 *   behind it and impossible to solve. It goes back to modal with the result.
 *
 * @extends {DialogComponent}
 */
class NewsletterPopup extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #timer;

  /** @type {boolean} */
  #pending = false;

  /** @type {boolean} */
  #waitingForCaptcha = false;

  /** Set for the one resubmit after waiting, so a captcha that never loads falls back to Shopify. */
  #captchaWaitDone = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const form = this.querySelector('form');
    if (form instanceof HTMLFormElement) {
      // The script owns the message, so skip the browser's validation bubble.
      form.noValidate = true;
      // Shopify's storefront captcha also listens for submit, cancels it and
      // later calls form.submit() (which fires no event), so a listener on the
      // form never runs. Window capture runs before it on the way down.
      window.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
      form.addEventListener('input', this.#handleInput, { signal });
      // The captcha script finishes with form.submit(); post in the background instead.
      form.submit = () => this.#post(form);
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
      return;
    }

    if (this.refs.dialog.dataset.state) {
      clearFormResultFromUrl();
      this.#open();
      return;
    }

    if (this.hasAttribute('data-disabled') || hasSeen()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    if (this.hasAttribute('data-mobile-scroll') && window.matchMedia(MOBILE_QUERY).matches) {
      this.#openAfterScroll(delay, signal);
      return;
    }
    this.#timer = window.setTimeout(() => this.#open(), delay);
  }

  /**
   * Starts the open delay once the visitor has scrolled one screen down.
   * @param {number} delay
   * @param {AbortSignal} signal
   */
  #openAfterScroll(delay, signal) {
    const onScroll = () => {
      if (window.scrollY < window.innerHeight) return;
      window.removeEventListener('scroll', onScroll);
      this.#timer = window.setTimeout(() => this.#open(), delay);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    window.clearTimeout(this.#timer);
  }

  #open() {
    markSeen();
    this.showDialog();
    this.#warmCaptcha();
  }

  /** Shopify binds its captcha on the form's first focusin; trigger that now. */
  #warmCaptcha() {
    this.#emailInput()?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
  }

  /**
   * Waits until Shopify's captcha has bound to the form, then submits again.
   * @param {HTMLFormElement} form
   */
  async #submitWhenCaptchaReady(form) {
    if (this.#waitingForCaptcha) return;
    this.#waitingForCaptcha = true;
    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    this.#warmCaptcha();

    const start = Date.now();
    while (!isCaptchaBound(form) && Date.now() - start < CAPTCHA_WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    this.#waitingForCaptcha = false;
    button?.removeAttribute('aria-busy');
    if (!this.isConnected) return;
    this.#captchaWaitDone = true;
    form.requestSubmit(button instanceof HTMLButtonElement ? button : undefined);
    this.#captchaWaitDone = false;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement) || event.target !== form) return;
    const input = this.#emailInput();
    if (input && !isValidEmail(input.value)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showError(input);
      input.focus();
      return;
    }

    // Captcha not bound yet: Shopify's script would swallow this submit.
    if (!isCaptchaBound(form) && !this.#captchaWaitDone) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#submitWhenCaptchaReady(form);
      return;
    }

    // Valid email: leave the event to Shopify's captcha, unless it already
    // added its token (a requestSubmit() after the check) - then post it here.
    this.#releaseModal();
    if (!hasCaptchaToken(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#post(form);
  };

  /**
   * Posts the form with fetch and shows the result from the page Shopify
   * answers with: the same popup, rendered with data-state and its message.
   * @param {HTMLFormElement} form
   */
  async #post(form) {
    if (this.#pending) return;
    this.#pending = true;
    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        signal: this.#abortController?.signal,
      });

      if (new URL(response.url).pathname.startsWith('/challenge')) {
        window.location.assign(response.url);
        return;
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const dialog = this.refs.dialog;
      const next = doc.getElementById(dialog.id);
      const nextBody = next?.querySelector('.cb-newsletter-popup__body');
      const body = dialog.querySelector('.cb-newsletter-popup__body');
      const state = next instanceof HTMLElement ? next.dataset.state : '';
      if (!state || !nextBody || !body) throw new Error('No newsletter result in the response');

      dialog.dataset.state = state;
      body.replaceWith(document.importNode(nextBody, true));
      this.#focusResult(state);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      this.#restoreModal();
      this.#pending = false;
      if (button instanceof HTMLButtonElement && button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** Non-modal while the captcha may need the page (see the class comment). */
  #releaseModal() {
    const { dialog } = this.refs;
    if (!dialog.open || !dialog.matches(':modal')) return;
    // Re-opening would replay the entrance animation; closeDialog() clears this.
    dialog.style.animation = 'none';
    dialog.classList.add('is-captcha');
    dialog.close();
    dialog.show();
  }

  #restoreModal() {
    const { dialog } = this.refs;
    if (!dialog.classList.contains('is-captcha')) return;
    dialog.classList.remove('is-captcha');
    if (!dialog.open) return;
    dialog.close();
    dialog.showModal();
  }

  /** @param {string} state */
  #focusResult(state) {
    if (state === 'error') {
      this.#emailInput()?.focus();
      return;
    }
    const heading = this.refs.dialog.querySelector('.cb-newsletter-popup__heading');
    if (heading instanceof HTMLElement) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }

  #handleInput = () => {
    const input = this.#emailInput();
    if (input?.getAttribute('aria-invalid') === 'true' && isValidEmail(input.value)) this.#clearError(input);
  };

  #emailInput() {
    const input = this.querySelector('input[name="contact[email]"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #errorElement() {
    return this.querySelector('.cb-newsletter-popup__error');
  }

  /** @param {HTMLInputElement} input */
  #showError(input) {
    input.setAttribute('aria-invalid', 'true');
    const error = this.#errorElement();
    if (!(error instanceof HTMLElement)) return;
    error.hidden = false;
    // Re-set the text so role="alert" announces a repeated failure too.
    error.textContent = '';
    error.textContent = error.dataset.errorText || '';
  }

  /** @param {HTMLInputElement} input */
  #clearError(input) {
    input.removeAttribute('aria-invalid');
    const error = this.#errorElement();
    if (error instanceof HTMLElement) error.hidden = true;
  }

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId) this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId) this.closeDialog();
  };
}

/**
 * Shopify redirects a successful signup to `?customer_posted=true#<form id>`,
 * and renders the form as successful for as long as that parameter is in the
 * URL. Removing it means a reload shows the plain page instead of the success
 * popup again.
 */
function clearFormResultFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('customer_posted') && !url.hash.startsWith('#CbNewsletterForm')) return;

  url.searchParams.delete('customer_posted');
  if (url.hash.startsWith('#CbNewsletterForm')) url.hash = '';
  window.history.replaceState(window.history.state, '', url);
}

/**
 * Stricter than the browser's type="email" check, which accepts "name@host"
 * that Shopify then rejects: requires a dot-separated domain.
 * @param {string} value
 */
function isValidEmail(value) {
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/.test(value.trim());
}

/**
 * True once Shopify's captcha (hCaptcha or reCAPTCHA) has put its token in the form.
 * @param {HTMLFormElement} form
 */
function hasCaptchaToken(form) {
  return ['h-captcha-response', 'g-recaptcha-response', 'recaptcha-v3-token'].some((name) => {
    const field = form.elements.namedItem(name);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field.value !== '' : false;
  });
}

/**
 * True once Shopify's captcha script has attached hCaptcha or reCAPTCHA to the form.
 * @param {HTMLFormElement} form
 */
function isCaptchaBound(form) {
  return Boolean(form.dataset.hcaptchaBound || form.dataset.recaptchaBound);
}

function hasSeen() {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    window.sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    // Storage blocked (private mode, disabled cookies): the popup may show again, which is harmless.
  }
}

if (!customElements.get('cb-newsletter-popup')) {
  customElements.define('cb-newsletter-popup', NewsletterPopup);
}
